/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import * as React from 'react';
import {
  faClipboardQuestion,
  faClipboardUser,
  faFilesMedical,
  faHeadSideHeart,
  faHome,
  faMessagesQuestion,
  faUsers,
  faStretcher,
  faWarning,
  faRightLeft,
  faDoorOpen,
  faBuilding,
  faMessageLines,
  faCalendarDay,
  faFolderGear,
  faBell,
  faDollarSign,
  faHospital,
  faUserTie,
  faTv,
  faStar,
} from '@fortawesome/pro-regular-svg-icons';
import { MenuGroup, MenuItem } from '@quality24/design-system';
import { LazyRouteFunction, RouteObject } from 'react-router';
import PrivateRoute from './PrivateRoute';

export type DashboardMenuItem = MenuItem & {
  /**
   * The identity policy permission
   */
  permission: string;
  /**
   * The lazy loading function
   */
  lazy: LazyRouteFunction<RouteObject>;
  /**
   * If routingOnly is true this item should not be rendered on the menu
   */
  routingOnly?: boolean;
};

export type DashboardMenuGroup = Omit<MenuGroup, 'items'> & {
  items: Array<DashboardMenuItem>;
};

export const groups: Array<DashboardMenuGroup> = [
  {
    id: 'group1',
    label: 'Histórico',
    items: [
      {
        id: 'item1',
        icon: faDoorOpen,
        children: 'Central de Indicadores',
        to: '/main/history/indicator',
        permission: 'inpatient:dashboard:page:keyPerformanceIndicator:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/PerformanceIndicatorPage/PerformanceIndicatorPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:keyPerformanceIndicator:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item2',
        icon: faFilesMedical,
        children: 'Solicitações',
        to: '/main/history/orders',
        permission: 'inpatient:dashboard:page:orderAnalysis:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/OrderHistoryPage/OrderHistoryPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:orderAnalysis:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item3',
        icon: faDollarSign,
        children: 'Solicitações Pagas',
        to: '/main/history/paid-orders',
        permission: 'inpatient:dashboard:page:paidOrderAnalysis:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/PaidOrderHistoryPage/PaidOrderHistoryPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:paidOrderAnalysis:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item4',
        icon: faHeadSideHeart,
        children: 'Experiência do Paciente',
        to: '/main/history/satisfaction',
        permission: 'inpatient:dashboard:page:experienceAnalysis:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/QualityHistoryPage/QualityHistoryPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:experienceAnalysis:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item5',
        icon: faMessagesQuestion,
        to: '/main/history/feedbacks',
        children: 'Canal de Manifestações',
        permission: 'feedback:dashboard:page:metricsDashboard:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/FeedbackHistoryPage/FeedbackHistoryPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="feedback:dashboard:page:metricsDashboard:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item6',
        icon: faStretcher,
        children: 'Giro de Leitos',
        to: '/main/history/roomManagement',
        permission: 'roomMgmt:dashboard:page:metricsDashboard:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/RoomMgmtHistoryPage/RoomMgmtHistoryPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="roomMgmt:dashboard:page:metricsDashboard:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item7',
        icon: faFilesMedical,
        children: 'Giro de Salas Cirúrgicas',
        to: '/main/history/surgery',
        permission: 'surgery:dashboard:page:metricsDashboard:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/SurgeryHistoryPage/SurgeryHistoryPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="surgery:dashboard:page:metricsDashboard:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item8',
        icon: faClipboardUser,
        children: 'Ficha de Pacientes',
        to: '/main/admissions/patients',
        permission: 'inpatient:dashboard:page:patientSearchList:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/PatientSearchPage/PatientSearchPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:patientSearchList:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item9',
        to: '/main/admissions/patients/:id',
        children: 'Paciente',
        permission: 'inpatient:dashboard:page:patientSearchList:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/PatientPage/PatientPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:patientSearchList:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
        routingOnly: true,
      },
      {
        id: 'item10',
        icon: faFolderGear,
        children: 'Facilities',
        to: '/main/history/facilities',
        permission: 'inpatient:dashboard:page:facilitiesAnalysis:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/CleaningOrdersHistoryPage/CleaningOrdersHistoryPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:facilitiesAnalysis:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item11',
        icon: faStar,
        children: 'Rounds de Liderança',
        to: '/main/history/leadership-rounds',
        permission: 'rounds:dashboard:page:roundAnalysis:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/LeadershipRoundsHistoryPage/LeadershipRoundsHistoryPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="rounds:dashboard:page:roundAnalysis:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item12',
        icon: faHospital,
        children: 'Indicadores de Hotelaria',
        to: '/main/history/facilitiesSurveyAnalysis',
        permission: 'facilities:dashboard:page:surveyHistory:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/FacilitiesSurveyHistoryPage/FacilitiesSurveyHistoryPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="facilities:dashboard:page:surveyHistory:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
    ],
  },
  {
    id: 'group2',
    label: 'Tempo Real',
    items: [
      {
        id: 'item1',
        icon: faFilesMedical,
        children: 'Solicitações',
        to: '/main/monitor/orders',
        permission: 'inpatient:dashboard:page:orderMonitor:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/OrderMonitorPage/OrderMonitorPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:orderMonitor:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item1.1',
        icon: faFilesMedical,
        children: 'Solicitações',
        to: '/main/monitor/orders/summary',
        permission: 'inpatient:dashboard:page:orderMonitor:view',
        routingOnly: true,
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/OrderSummaryMonitorPage/OrderSummaryMonitorPage'
          );
          return {
            Component: () => (
              <PrivateRoute
                action="inpatient:dashboard:page:orderSummaryMonitor:view"
                redirectTo="/main/monitor/orders"
              >
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item2',
        icon: faHeadSideHeart,
        children: 'Experiência do Paciente',
        to: '/main/monitor/experience',
        permission: 'inpatient:dashboard:page:experienceMonitor:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/QualityMonitorPage/QualityMonitorPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:experienceMonitor:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item4',
        icon: faMessagesQuestion,
        to: '/main/monitor/feedbacks',
        children: 'Canal de Manifestações',
        permission: 'feedback:dashboard:page:feedbackMonitor:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/FeedbackMonitorPage/FeedbackMonitorPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="feedback:dashboard:page:feedbackMonitor:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item4.1',
        icon: faMessagesQuestion,
        to: '/main/monitor/feedbacks/:id',
        children: 'Canal de Manifestações',
        permission: 'feedback:dashboard:page:feedbackDetails:view',
        routingOnly: true,
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/FeedbackDetailsPage/FeedbackDetailsPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="feedback:dashboard:page:feedbackDetails:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item4.2',
        to: '/main/monitor/feedbacks/new',
        children: 'Canal de Manifestações',
        permission: 'feedback:dashboard:page:createFeedback:view',
        routingOnly: true,
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/FeedbackFormPage/FeedbackFormPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="feedback:dashboard:page:createFeedback:view">
                <Component mode="create" />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item4.3',
        to: '/main/monitor/feedbacks/:id/edit',
        children: 'Canal de Manifestações',
        permission: 'feedback:dashboard:page:createFeedback:view',
        routingOnly: true,
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/FeedbackFormPage/FeedbackFormPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="feedback:dashboard:page:createFeedback:view">
                <Component mode="update" />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item6',
        icon: faStretcher,
        children: 'Giro de Leitos',
        to: '/main/monitor/roomManagement',
        permission: 'roomMgmt:dashboard:page:roomMonitor:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/RoomMgmtMonitorPage/RoomMgmtMonitorPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="roomMgmt:dashboard:page:roomMonitor:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item7',
        icon: faDoorOpen,
        children: 'Giro de Salas Cirúrgicas',
        to: '/main/monitor/surgery',
        permission: 'surgery:dashboard:page:roomMonitor:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/SurgeryMonitorPage/SurgeryMonitorPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="surgery:dashboard:page:roomMonitor:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item8',
        icon: faDoorOpen,
        children: 'Fluxo Pré-Operatório',
        to: '/main/monitor/pre-op-workflow',
        permission: 'surgery:dashboard:page:preOpWorkflowMonitor:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/SurgeryWorkflowMonitorPage/SurgeryWorkflowMonitorPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="surgery:dashboard:page:preOpWorkflowMonitor:view">
                <Component type="PRE_OP" />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item9',
        icon: faDoorOpen,
        children: 'Fluxo Pós-Operatório',
        to: '/main/monitor/post-op-workflow',
        permission: 'surgery:dashboard:page:postOpWorkflowMonitor:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/SurgeryWorkflowMonitorPage/SurgeryWorkflowMonitorPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="surgery:dashboard:page:postOpWorkflowMonitor:view">
                <Component type="POST_OP" />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item10',
        icon: faTv,
        children: 'Painel de Acompanhamento',
        to: '/main/monitor/surgeries/family-dashboard',
        permission: 'surgery:dashboard:page:familyDashboard:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/SurgeryProgressForFamilyPage/SurgeryProgressForFamilyPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="surgery:dashboard:page:familyDashboard:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item11',
        icon: faWarning,
        children: 'Alertas',
        to: '/main/monitor/surveyAlerts',
        permission: 'survey:dashboard:page:feedbackAlertMonitor:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/FeedbackAlertMonitorPage/FeedbackAlertMonitorPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="survey:dashboard:page:feedbackAlertMonitor:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item12',
        icon: faRightLeft,
        children: 'Registro de Movimentações',
        to: '/main/admissions/transfers',
        permission: 'inpatient:dashboard:page:transferLogMonitor:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/TransferLogPage/TransferLogPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:transferLogMonitor:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item13',
        icon: faBuilding,
        children: 'Indicadores de Hotelaria',
        to: '/main/facilities/surveys',
        permission: 'facilities:dashboard:page:surveyMonitor:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/FacilitiesSurveyListPage/FacilitiesSurveyListPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="facilities:dashboard:page:surveyMonitor:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item14',
        routingOnly: true,
        children: 'Indicadores de Hotelaria',
        to: '/main/facilities/surveys/:id',
        permission: 'facilities:dashboard:survey:edit',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/FacilitiesSurveyPage/FacilitiesSurveyPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="facilities:dashboard:survey:edit">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },

      // Operational Pages
      {
        id: 'item100',
        icon: faHome,
        children: 'Portal de Solicitações',
        to: '/main/demandCenter/groups',
        permission: 'demandCenter:dashboard:page:portal:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/demandCenter/RequestsPortalGroupsPage/RequestsPortalGroupsPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="demandCenter:dashboard:page:portal:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item101',
        routingOnly: true,
        children: 'Portal de Solicitações',
        to: '/main/demandCenter/orders',
        permission: 'demandCenter:dashboard:page:portal:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/demandCenter/RequestsPortalCreatePage/RequestsPortalCreatePage'
          );
          return {
            Component: () => (
              <PrivateRoute action="demandCenter:dashboard:page:portal:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item102',
        icon: faClipboardQuestion,
        children: 'Pesquisas disponíveis',
        to: '/surveys/answer',
        permission: 'survey:dashboard:page:answerSurvey:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/SurveySelectorPage/SurveySelectorPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="survey:dashboard:page:answerSurvey:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item103',
        to: '/surveys/answer/:surveyId',
        children: 'Pesquisa',
        permission: 'survey:dashboard:page:answerSurvey:view',
        routingOnly: true,
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/SurveyPage/SurveyPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="survey:dashboard:page:answerSurvey:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
    ],
  },
  {
    id: 'group3',
    label: 'Administração',
    items: [
      {
        id: 'item1',
        icon: faUsers,
        children: 'Colaboradores',
        to: '/admin/employees',
        permission: 'inpatient:dashboard:page:employees:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/RegisterEmployeePage/RegisterEmployeePage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:employees:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item2',
        icon: faUsers,
        children: 'Contas',
        to: '/admin/accounts',
        permission: 'inpatient:dashboard:page:accounts:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/RegisterAccountPage/RegisterAccountPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:accounts:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item3',
        icon: faMessageLines,
        children: 'Manifestações',
        to: '/admin/feedbacks',
        permission: 'feedback:dashboard:page:createDepartmentManager:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/RegisterDepartmentManagerPage/RegisterDepartmentManagerPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="feedback:dashboard:page:createDepartmentManager:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item4',
        icon: faBell,
        to: '/admin/order-schedules',
        children: 'Programação de Solicitações',
        permission: 'inpatient:dashboard:page:orderSchedules:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/OrderScheduleListPage/OrderScheduleListPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:orderSchedules:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item41',
        icon: faBell,
        to: '/admin/order-schedules/:id',
        children: 'Nova Programação',
        permission: 'inpatient:dashboard:page:orderSchedulesForm:view',
        routingOnly: true,
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/OrderScheduleFormPage/OrderScheduleFormPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:orderSchedulesForm:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item5',
        icon: faCalendarDay,
        children: 'Feriados',
        to: '/admin/holidays',
        permission: 'inpatient:dashboard:page:holidays:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/HolidayPage/HolidayPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:holidays:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item51',
        icon: faUserTie,
        children: 'Rounds de Liderança',
        to: '/admin/rounds',
        permission: 'rounds:dashboard:page:registration:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/RoundsRegistrationPage/RoundsRegistrationPage'
          );
          return {
            Component: () => (
              <PrivateRoute action="rounds:dashboard:page:registration:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
      {
        id: 'item6',
        icon: faBuilding,
        children: 'Estrutura',
        to: '/admin/hospital-structure',
        permission: 'inpatient:dashboard:page:hospitalStructure:view',
        lazy: async () => {
          const { Component } = await import(
            '@/inpatient-dashboard/pages/HospitalStructurePage/HospitalStructurePage'
          );
          return {
            Component: () => (
              <PrivateRoute action="inpatient:dashboard:page:hospitalStructure:view">
                <Component />
              </PrivateRoute>
            ),
          };
        },
      },
    ],
  },
];

export default groups;
